"use client";

import { cn } from "@/lib/utils";

interface PasswordStrengthProps {
  password: string;
  className?: string;
}

type StrengthLevel = {
  label: string;
  color: string;
  bars: number;
  description: string;
};

function calculatePasswordStrength(password: string): StrengthLevel {
  if (password.length === 0) {
    return {
      label: "",
      color: "bg-border",
      bars: 0,
      description: "",
    };
  }

  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const length = password.length;

  // Weak: < 8 characters
  if (length < 8) {
    return {
      label: "Weak",
      color: "bg-destructive",
      bars: 1,
      description: "Too short - at least 8 characters required",
    };
  }

  // Medium: 8-11 characters OR missing required character types
  if (length < 12 || !hasUppercase || !hasLowercase || !hasNumber) {
    return {
      label: "Medium",
      color: "bg-warning",
      bars: 2,
      description: "Add uppercase, lowercase, and numbers",
    };
  }

  // Very Strong: 12+ characters with all types AND special characters - CYAN GLOW
  if (hasUppercase && hasLowercase && hasNumber && hasSpecial && length >= 12) {
    return {
      label: "Very Strong",
      color: "bg-accent shadow-[0_0_20px_hsl(168,100%,45%,0.5)] transition-shadow duration-300",
      bars: 3,
      description: "Exceptional password strength!",
    };
  }

  // Strong: 12+ characters with all basic types (no special)
  if (hasUppercase && hasLowercase && hasNumber) {
    return {
      label: "Strong",
      color: "bg-success",
      bars: 3,
      description: "Excellent password strength",
    };
  }

  // Fallback (should not reach here)
  return {
    label: "Weak",
    color: "bg-destructive",
    bars: 1,
    description: "Password does not meet requirements",
  };
}

export function PasswordStrength({ password, className }: PasswordStrengthProps) {
  const strength = calculatePasswordStrength(password);

  if (!password) {
    return null;
  }

  return (
    <div className={cn("space-y-2", className)}>
      {/* Strength bars */}
      <div className="flex gap-1">
        {[1, 2, 3].map((bar) => (
          <div
            key={bar}
            className={cn(
              "h-1 flex-1 rounded-full transition-all duration-300",
              bar <= strength.bars ? strength.color : "bg-border"
            )}
          />
        ))}
      </div>

      {/* Strength label and description */}
      {strength.label && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Password strength: <span className="font-medium text-foreground">{strength.label}</span>
          </span>
        </div>
      )}

      {strength.description && (
        <p className="text-xs text-muted-foreground">
          {strength.description}
        </p>
      )}
    </div>
  );
}
